import { Popover } from "@headlessui/react";
import { mdiClose, mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

interface IProps {
  align?: "left" | "right";
}

export default function PendingBalanceExplainer({ align = "left" }: Readonly<IProps>) {
  return (
    <Popover className="relative inline-flex">
      {({ open }) => (
        <>
          <Popover.Button
            aria-label="What makes up your pending balance"
            className={clsx(
              "inline-flex items-center justify-center rounded-full size-[17px] border outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white",
              open
                ? "bg-white border-white text-homeCardEnd"
                : "bg-white/20 border-white/55 text-white hover:bg-white/40"
            )}>
            <Icon path={mdiInformationOutline} size={0.5} />
          </Popover.Button>

          <Popover.Panel
            className={clsx(
              "absolute top-full z-50 mt-3 w-80 max-w-[calc(100vw-4rem)] cursor-default",
              align === "right" ? "right-0" : "left-0"
            )}>
            {({ close }) => (
              <div className="relative p-4 text-xs leading-relaxed text-left border rounded-lg shadow-lg border-border/20 bg-background text-text">
                <span
                  aria-hidden
                  className={clsx(
                    "absolute size-2.5 rotate-45 -top-[0.3125rem] border-t border-l border-border/20 bg-background",
                    align === "right" ? "right-4" : "left-4"
                  )}
                />

                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm font-bold">What makes up your pending balance</span>
                  <button
                    type="button"
                    aria-label="Close"
                    className="shrink-0 opacity-60 hover:opacity-100"
                    onClick={() => close()}>
                    <Icon path={mdiClose} size={0.7} />
                  </button>
                </div>

                <p className="mt-2">
                  Money on its way out of your wallet — scheduled, but not collected yet.
                </p>
                <p className="mt-2">
                  When a funding request is approved we schedule a withdrawal from your wallet for
                  the date your payment terms fall due. Your carrier gets paid once that withdrawal
                  clears — not before.
                </p>

                <p className="mt-3 font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-subtitle">
                  Each withdrawal is made up of
                </p>
                <div className="flex flex-col gap-1.5 p-3 mt-1.5 border rounded border-border/15 bg-card">
                  <span className="flex items-baseline gap-2">
                    <i className="w-2 font-mono font-bold not-italic text-center shrink-0 text-primary">
                      +
                    </i>
                    What your carrier gets paid for the load
                  </span>
                  <span className="flex items-baseline gap-2">
                    <i className="w-2 font-mono font-bold not-italic text-center shrink-0 text-primary">
                      +
                    </i>
                    Your HaulPay self-finance fee
                  </span>
                </div>

                <p className="mt-2.5">
                  Your pending balance is all of those scheduled withdrawals added up — including
                  any that are already past their due date.
                </p>

                <div className="pt-2.5 mt-3 border-t border-border/15">
                  <p className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-subtitle">
                    These numbers aren&apos;t final
                  </p>
                  <p className="mt-1">
                    We work them out again every time a self-finance funding request is approved.
                    New loads don&apos;t only land in the later windows — approve one at 1-day speed
                    and today&apos;s total goes up straight away. If a load&apos;s payment speed
                    changes, its withdrawal moves to the new date with it.
                  </p>
                </div>

                <div className="pt-2.5 mt-3 border-t border-border/15">
                  <p className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-subtitle">
                    Good to know
                  </p>
                  <p className="mt-1">
                    It hasn&apos;t left your Current Balance yet, and it doesn&apos;t count money
                    your customers are paying in. It&apos;s shown in parentheses because it&apos;s
                    money going out.
                  </p>
                </div>
              </div>
            )}
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
}
